import { useQuery } from '@apollo/client';
import { useI18n } from '@i18n';
import { PBreadcrumbs } from '@/components/molecules/Breadcrumbs/PBreadcrumbs';
import { useSession } from '@/components/session/useSession';
import { GET_FOLDER_BREADCRUMB } from '@/graphql/Drive.queries';
import { PATHS } from '@/routes';
import { getDisambiguatedFolderName } from '@/sections/Drive/utils/folderUtils';

type FolderBreadcrumbProps = {
  currentFolderId: number | null;
};

export default function FolderBreadcrumb({ currentFolderId }: FolderBreadcrumbProps) {
  const { t } = useI18n();
  const {
    org: { orgSector },
  } = useSession();

  const { data, previousData } = useQuery(GET_FOLDER_BREADCRUMB, {
    variables: { folderId: currentFolderId! },
    skip: currentFolderId === null,
    fetchPolicy: 'cache-and-network',
  });

  // `previousData` smooths over refetch flicker, but when the query is skipped
  // (at the root, currentFolderId === null) it would otherwise freeze the
  // breadcrumb on the last folder's stale path. Only fall back while actually
  // viewing a folder.
  const effectiveData = currentFolderId === null ? undefined : (data ?? previousData);
  const breadcrumbPath = (effectiveData?.getFolderBreadcrumb?.breadcrumb ?? []).filter(
    (segment) => segment.folderTypeId !== 'ROOT',
  );

  return (
    <PBreadcrumbs
      links={[
        {
          name: t('General_Drive'),
          href: PATHS.drive.root(),
        },
        ...breadcrumbPath.map((segment, index) => {
          // The previous crumb is this segment's parent, so reuse the tree's same-name disambiguation.
          const parentName = index > 0 ? breadcrumbPath[index - 1].name : null;
          return {
            name: getDisambiguatedFolderName(
              { name: segment.name, folder_type_id: segment.folderTypeId },
              parentName,
              orgSector,
            ),
            ...(index < breadcrumbPath.length - 1 ? { href: PATHS.drive.folder({ id: segment.id }) } : {}),
          };
        }),
      ]}
    />
  );
}
